import Box from '@/common/components/Box'
import TextField from '@/common/components/TextField'
import useDebounce from '@/common/hooks/useDebounce'
import { cn } from '@/common/utils/cn'
import { formatPercentage } from '@/common/utils/formatters'
import {
  integrationStatusColorClass,
  integrationStatusLabels,
} from '@/features/equipmentModels/constants/integrationStatusOptions.constant'
import { useEquipmentModelIntegrationCode } from '@/features/equipmentModels/hooks/useEquipmentModelIntegrationCode'
import { useEquipmentModelIntegrationCodePieces } from '@/features/equipmentModels/hooks/useEquipmentModelIntegrationCodePieces'
import PieceCard from '@/features/equipmentModels/pages/Show/components/IntegrationsTab/PieceCard'
import { IntegrationCodePiece } from '@/features/equipmentModels/types/equipmentModelIntegrationCode'
import { Search } from 'lucide-react'
import { ChangeEvent } from 'react'

interface IntegrationDetailsProps {
  integrationCodeId?: string
}

const IntegrationDetails = ({ integrationCodeId }: IntegrationDetailsProps) => {
  const [piecesSearch, setPiecesSearch] = useDebounce('')

  const { data: integrationCode } = useEquipmentModelIntegrationCode({
    id: integrationCodeId,
    refetchInterval: (query) =>
      query.state.data?.data?.data?.integration_status === 'link_in_progress' ? 30000 : false,
  })

  const isInProgress = integrationCode?.integration_status === 'link_in_progress'

  const { data: pieces = [], isPending } = useEquipmentModelIntegrationCodePieces<IntegrationCodePiece[]>({
    integrationCodeId,
    filters: { search: piecesSearch },
    enabled: !!integrationCodeId,
    refetchInterval: isInProgress ? 30000 : false,
  })

  if (!integrationCode) {
    return <p className="italic text-xs text-white-400">Selecione uma integração para ver os detalhes.</p>
  }

  const statusLabel = integrationStatusLabels[integrationCode.integration_status]
  const statusColor = integrationStatusColorClass[integrationCode.integration_status]
  const progress = formatPercentage(Number(integrationCode.linking_progress_percentage))
  const statusValue = isInProgress ? progress : statusLabel

  return (
    <div className="flex flex-col gap-2">
      <Box className="flex flex-col gap-2">
        <p className="text-sm font-bold">Detalhes da integração</p>

        <div className="flex flex-col gap-1 p-2 bg-white-200 rounded">
          <p className={cn('text-[10px] font-light leading-3', statusColor)}>Status</p>

          <p className={cn('text-xs font-bold', statusColor)}>{statusValue}</p>
        </div>

        {integrationCode?.integration_status === 'failed_on_linking' && (
          <div className="flex flex-col gap-1 p-2 bg-white-200 rounded">
            <p className="text-[10px] font-light leading-3 text-danger-soft">Motivo da falha</p>

            <p className="text-xs font-bold text-danger-soft">
              {integrationCode?.additional_attributes?.failure_reason || '--'}
            </p>
          </div>
        )}

        <div className="flex flex-col gap-1 p-2 bg-white-200 rounded">
          <p className={cn('text-[10px] font-light leading-3', statusColor)}>Partes carregadas</p>

          <p className={cn('text-xs font-bold', statusColor)}>
            {integrationCode?.additional_attributes?.linked_parts_count ?? 0}/
            {integrationCode?.additional_attributes?.parts_count ?? 0} Partes
          </p>
        </div>
      </Box>

      <Box className="flex flex-col gap-2">
        <p className="text-sm font-bold">Peças carregadas</p>

        <TextField
          placeholder="Busque por Nome"
          onChange={(event: ChangeEvent<HTMLInputElement>) => setPiecesSearch(event.target.value)}
          LeftIcon={<Search />}
          dataTestid="pieces_search_input"
        />

        {isPending ? (
          <p className="italic text-xs">Carregando peças...</p>
        ) : pieces.length === 0 ? (
          <p className="italic text-xs text-white-400">Nenhuma peça carregada.</p>
        ) : (
          <div className="flex flex-col gap-2">
            {pieces.map((piece) => (
              <PieceCard key={piece.id} piece={piece} />
            ))}
          </div>
        )}
      </Box>
    </div>
  )
}

export default IntegrationDetails
